import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
// Public routes that must NOT trigger a redirect to /login on 401.
// Keep this list in sync with contexts/AuthContext.jsx and route registration in App.js.
const PUBLIC_ROUTE_PREFIXES = ['/login', '/auth/callback', '/landing/', '/trust', '/verify/', '/insights', '/perfil/', '/super-admin'];
const isPublicRoute = (path) => PUBLIC_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p.endsWith('/') ? p : p + '/') || path.startsWith(p));

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      const path = window.location.pathname;
      if (!isPublicRoute(path)) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper functions for common API calls
export const assetsApi = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  import: (assets) => api.post('/assets/import', assets),
  getStats: () => api.get('/assets/stats/summary'),
};

export const requirementsApi = {
  getAll: (params) => api.get('/requirements', { params }),
  getById: (id) => api.get(`/requirements/${id}`),
  create: (data) => api.post('/requirements', data),
  updateStatus: (id, status) => api.put(`/requirements/${id}/status`, { status }),
};

export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getStats: () => api.get('/tasks/stats/summary'),
};

export const evidenceApi = {
  getAll: (params) => api.get('/evidence', { params }),
  getById: (id) => api.get(`/evidence/${id}`),
  create: (data) => api.post('/evidence', data),
  delete: (id) => api.delete(`/evidence/${id}`),
};

export const incidentsApi = {
  getAll: (params) => api.get('/incidents', { params }),
  create: (data) => api.post('/incidents', data),
  update: (id, data) => api.put(`/incidents/${id}`, data),
};

export const risksApi = {
  getAll: (params) => api.get('/risks', { params }),
  create: (data) => api.post('/risks', data),
};

export const vulnerabilitiesApi = {
  getAll: (params) => api.get('/vulnerabilities', { params }),
  getStats: () => api.get('/vulnerabilities/stats'),
  create: (data) => api.post('/vulnerabilities', data),
  update: (id, data) => api.put(`/vulnerabilities/${id}`, data),
  delete: (id) => api.delete(`/vulnerabilities/${id}`),
  syncScanner: (scanner, count) => api.post('/vulnerabilities/scan/sync', { scanner, count }),
};

export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
};

export const usersApi = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  // Admin-only management
  list: (params = {}) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (body) => api.post('/auth/register', body),
  update: (id, body) => api.patch(`/users/${id}`, body),
  deactivate: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

export const seedData = () => api.post('/seed-data');

// --- EU AI Act ---
export const aiActApi = {
  // F1 Systems
  listSystems: (params) => api.get('/ai-act/systems', { params }),
  getSystem: (id) => api.get(`/ai-act/systems/${id}`),
  createSystem: (data) => api.post('/ai-act/systems', data),
  updateSystem: (id, data) => api.put(`/ai-act/systems/${id}`, data),
  deleteSystem: (id) => api.delete(`/ai-act/systems/${id}`),
  systemStats: () => api.get('/ai-act/systems/stats'),
  // F2 Classification
  classifySystem: (id, answers) => api.post(`/ai-act/systems/${id}/classify`, answers),
  classifyPreview: (answers) => api.post('/ai-act/classify/preview', answers),
  // F3 Risks
  listRisks: (params) => api.get('/ai-act/risks', { params }),
  riskStats: (params) => api.get('/ai-act/risks/stats', { params }),
  createRisk: (data) => api.post('/ai-act/risks', data),
  updateRisk: (id, data) => api.put(`/ai-act/risks/${id}`, data),
  deleteRisk: (id) => api.delete(`/ai-act/risks/${id}`),
  // F4 Data Governance
  getDataGov: (id) => api.get(`/ai-act/data-governance/${id}`),
  saveDataGov: (id, data) => api.put(`/ai-act/data-governance/${id}`, data),
  // F5 Tech Doc
  getTechDoc: (id) => api.get(`/ai-act/tech-docs/${id}`),
  saveTechDoc: (id, data) => api.put(`/ai-act/tech-docs/${id}`, data),
  // F6 Audit
  listAudit: (params) => api.get('/ai-act/audit', { params }),
  createAudit: (data) => api.post('/ai-act/audit', data),
  // F9 Conformity
  getConformity: (id) => api.get(`/ai-act/conformity/${id}`),
  // F10 EU Registry
  getEuRegistry: (id) => api.get(`/ai-act/eu-registry/${id}`),
  saveEuRegistry: (id, data) => api.put(`/ai-act/eu-registry/${id}`, data),
  submitEuRegistry: (id) => api.post(`/ai-act/eu-registry/${id}/submit`),
  // F11 Incidents
  listIncidents: (params) => api.get('/ai-act/incidents', { params }),
  createIncident: (data) => api.post('/ai-act/incidents', data),
  updateIncident: (id, data) => api.put(`/ai-act/incidents/${id}`, data),
  // F12 GPAI
  getGpai: (id) => api.get(`/ai-act/gpai/${id}`),
  saveGpai: (id, data) => api.put(`/ai-act/gpai/${id}`, data),
  // Dashboard
  dashboardStats: () => api.get('/ai-act/dashboard/stats'),
  // AI Risk Analyzer (LLM)
  suggestRisks: (id, body) => api.post(`/ai-act/risks/suggest/${id}`, body || {}),
  // AI Auto-drafters
  autoDraftTechDoc: (id, body) => api.post(`/ai-act/tech-docs/${id}/auto-draft`, body || {}),
  autoDraftTransparency: (id, body) => api.post(`/ai-act/transparency/${id}/auto-draft`, body || {}),
  // F7 Transparency
  getTransparency: (id) => api.get(`/ai-act/transparency/${id}`),
  saveTransparency: (id, data) => api.put(`/ai-act/transparency/${id}`, data),
  // F8 Human Oversight
  getHumanOversight: (id) => api.get(`/ai-act/human-oversight/${id}`),
  saveHumanOversight: (id, data) => api.put(`/ai-act/human-oversight/${id}`, data),
  // F13 Post-Market
  listPostMarket: (params) => api.get('/ai-act/post-market', { params }),
  createPostMarket: (data) => api.post('/ai-act/post-market', data),
  deletePostMarket: (id) => api.delete(`/ai-act/post-market/${id}`),
  autoAggregatePostMarket: (id, days) => api.post(`/ai-act/post-market/auto-aggregate/${id}?days=${days}`),
  // PDF Reports
  pdfSummary: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/pdf-reports/ai-act/${id}/summary`,
  pdfAnnexIV: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/pdf-reports/ai-act/${id}/annex-iv`,
};

// --- Sidebar Badges ---
export const badgesApi = {
  get: () => api.get('/notifications/badges'),
};

// --- AI Compliance Co-pilot ---
export const copilotApi = {
  chat: (body) => api.post('/ai-act/copilot/chat', body),
  getSession: (id) => api.get(`/ai-act/copilot/sessions/${id}`),
  deleteSession: (id) => api.delete(`/ai-act/copilot/sessions/${id}`),
  validate: (systemId) => api.get(`/ai-act/copilot/validate/${systemId}`),
};

// --- AI Health Score ---
export const healthScoreApi = {
  get: () => api.get('/ai-act/health-score'),
  refresh: () => api.post('/ai-act/health-score/refresh'),
};

// --- Compliance Roadmap (90-day plan generator) ---
export const roadmapApi = {
  generate: (body) => api.post('/ai-act/roadmap/generate', body || {}),
  list: () => api.get('/ai-act/roadmap'),
  get: (id) => api.get(`/ai-act/roadmap/${id}`),
  progress: (id) => api.get(`/ai-act/roadmap/${id}/progress`),
  apply: (id) => api.post(`/ai-act/roadmap/${id}/apply`),
  delete: (id) => api.delete(`/ai-act/roadmap/${id}`),
};

// --- Users management (admin-only) ---
// (see combined usersApi export above)

// --- User Management Audit Log (admin-only) ---
export const userAuditApi = {
  list: (params = {}) => api.get('/audit-log/user-management', { params }),
  stats: () => api.get('/audit-log/user-management/stats'),
  exportCsv: (params = {}) => api.get('/audit-log/user-management/export.csv', {
    params,
    responseType: 'blob',
  }),
  exportPdf: (params = {}) => api.get('/audit-log/user-management/export.pdf', {
    params,
    responseType: 'blob',
  }),
};
